import React from 'react';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import { ACTIVITY_TYPES, RESOURCES } from '@shared/Resources/types';
import { getAgeText, getUserTooltipText } from '@containers/Admin/containers/UserPage/helpers';
import { getStyledActivityIcon, StatusWrapper } from './styles';
import {t} from 'i18next'

export const getTitle = (message) => {
  return (
      <Text
          size={TEXT_SIZE.T5}
          textColor={TEXT_COLOR.DISABLED}
          textTransform='uppercase'
      >
        {t(message)}
      </Text>
  );
}

export const getUserHeaderTitle = (label = 'User', { uppercaseLabel = true } = {}) => (
  <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
    {uppercaseLabel ? (
      <Text
        size={TEXT_SIZE.T5}
        textColor={TEXT_COLOR.DISABLED}
        textTransform='uppercase'
      >
        {label}
      </Text>
    ) : (
      <span>{label}</span>
    )}
    <Text
      size={TEXT_SIZE.H6}
      textColor={TEXT_COLOR.DISABLED_LIGHT}
      margin='0 0 0 4px'
      textTransform='lowercase'
    >
      | age
    </Text>
  </span>
);

export const getCellText = (data, highlighted = false) => {
  return (
    <Text
      size={TEXT_SIZE.H4}
      textColor={highlighted ? TEXT_COLOR.DISABLED : TEXT_COLOR.NORMAL}
      textTransform='capitalize'
    >
      {Array.isArray(data) && data.join(', ')}
      {typeof data === 'string' && data}
    </Text>
  )
};

export const getUserCellText = (user, highlighted = false, TooltipComponent, tooltipProps = {}) => {
  if (!user) return getCellText('');

  const fullName = typeof user === 'string' ? user : user.fullName;
  const ageText = typeof user === 'string' ? '' : getAgeText(user);
  const tooltipText = typeof user === 'string' ? fullName : getUserTooltipText(user);

  const nameContent = (
    <Text
      size={TEXT_SIZE.H4}
      textColor={highlighted ? TEXT_COLOR.DISABLED : TEXT_COLOR.NORMAL}
    >
      {fullName}
    </Text>
  );

  const nameWithTooltip = TooltipComponent ? (
    <TooltipComponent text={tooltipText} {...tooltipProps}>
      {nameContent}
    </TooltipComponent>
  ) : nameContent;

  if (!ageText) {
    return nameWithTooltip;
  }

  return (
    <div>
      {nameWithTooltip}
      <Text
        size={TEXT_SIZE.T5}
        textColor={TEXT_COLOR.DISABLED}
      >
        | {ageText}
      </Text>
    </div>
  );
};

// Helper function to mimic CSS text-transform: capitalize behavior
const capitalizeText = (text) => {
  if (!text) return text;
  
  // Mimic CSS text-transform: capitalize exactly
  // Only capitalize the first letter of each word, preserve other letters as-is
  return String(text).replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getCellTextWithTooltip = (data, highlighted = false, TooltipComponent, tooltipProps = {}) => {
  const textContent = getCellText(data, highlighted);
  
  if (TooltipComponent && data) {
    // Apply the same capitalization as the CSS text-transform: capitalize
    let capitalizedText;
    if (typeof data === 'string') {
      capitalizedText = capitalizeText(data);
    } else if (Array.isArray(data)) {
      capitalizedText = capitalizeText(data.join(', '));
    } else {
      capitalizedText = data;
    }
        
    return (
      <TooltipComponent text={capitalizedText} {...tooltipProps}>
        {textContent}
      </TooltipComponent>
    );
  }
  
  return textContent;
};

export const STATUS_TYPES = {
  DONE: 'Done',
  FAILED: 'Failed',
  DISMISSED: 'Dismissed',
  INCOMPLETE: 'Incomplete',
  NO: 'No',
  YES: 'Yes',
  SENT: 'Sent',
  SKIPPED: 'Skipped',
};

export const getStatusText = (text, type, userActivityPage) => {
  let textColor;
  switch (type) {
    case STATUS_TYPES.DONE:
      textColor = TEXT_COLOR.COMPLETED;
      break;
    case STATUS_TYPES.FAILED:
      textColor = TEXT_COLOR.WARNING;
      break;
    case STATUS_TYPES.DISMISSED:
      textColor = TEXT_COLOR.DISABLED;
      break;
    case STATUS_TYPES.SKIPPED:
      textColor = TEXT_COLOR.DARK_GRAY;
      break;
    default:
      textColor = TEXT_COLOR.NORMAL;
  }
  if (userActivityPage) {
    return (
      <StatusWrapper type={STATUS_TYPES.DISMISSED}>
        <Text
          size={TEXT_SIZE.H5}
          textColor={TEXT_COLOR.DISABLED}
          textTransform='capitalize'
        >
          {text}
        </Text>
      </StatusWrapper>
    );
  }
  return (
    <StatusWrapper type={type}>
      <Text
        size={TEXT_SIZE.H5}
        textColor={textColor}
        textTransform='capitalize'
      >
        {text}
      </Text>
    </StatusWrapper>
  );
};

export const getActivityTypeTitle = (item) => {
  const ActivityIcon = getStyledActivityIcon(item.type);
  let text;
  switch (item.type) {
    case ACTIVITY_TYPES.ACTIVITY_PRACTICE:
      text = RESOURCES.PRACTICE;
      break;
    case ACTIVITY_TYPES.ACTIVITY_POLL:
      text = RESOURCES.POLL;
      break;
    default:
      text = 'UNKNOWN';
  }

  return (
    <div>
      <ActivityIcon />
      <Text size={TEXT_SIZE.H5} textColor={TEXT_COLOR.DISABLED} margin='0 5px'>
        {text}
      </Text>
    </div>
  );
};
